/**
 * Hook for real-time analytics updates via WebSocket.
 *
 * Subscribes to the `analytics:live` topic on mount, listens for
 * `analytics_snapshot` and `publication_engagement` events emitted
 * by the websocketService, and keeps the analyticsStore in sync.
 *
 * Usage:
 * ```typescript
 * const { liveSnapshot, engagementEvents, isConnected, lastUpdated } =
 *   useAnalyticsRealtime();
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { websocketService } from '../services/websocket';
import { useAnalyticsStore } from '../store/analyticsStore';
import { useAuthStore } from '../store/authStore';
import type { LiveAnalyticsSnapshot, PublicationEngagementEvent } from '../types';

interface UseAnalyticsRealtimeOptions {
  /** Maximum number of publication engagement events to keep in the buffer */
  maxEngagementEvents?: number;
}

interface UseAnalyticsRealtimeReturn {
  liveSnapshot: LiveAnalyticsSnapshot | null;
  engagementEvents: PublicationEngagementEvent[];
  isConnected: boolean;
  lastUpdated: string | null;
}

const ANALYTICS_TOPIC = 'analytics:live';

export function useAnalyticsRealtime(
  options: UseAnalyticsRealtimeOptions = {}
): UseAnalyticsRealtimeReturn {
  const maxEngagementEventsRef = useRef(options.maxEngagementEvents ?? 10);
  maxEngagementEventsRef.current = options.maxEngagementEvents ?? 10;

  const [liveSnapshot, setLiveSnapshot] = useState<LiveAnalyticsSnapshot | null>(null);
  const [engagementEvents, setEngagementEvents] = useState<PublicationEngagementEvent[]>([]);
  const [isConnected, setIsConnected] = useState(websocketService.isConnected());
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  // Keep a stable ref to the store setter so the effect deps never change
  const setStoreSnapshotRef = useRef(useAnalyticsStore.getState().setSnapshot);
  useEffect(() => {
    return useAnalyticsStore.subscribe(
      (state) => { setStoreSnapshotRef.current = state.setSnapshot; }
    );
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    const handleSnapshot = (data: LiveAnalyticsSnapshot) => {
      if (!isMountedRef.current) return;
      setLiveSnapshot(data);
      setStoreSnapshotRef.current(data);
      setLastUpdated(data.last_updated ?? new Date().toISOString());
    };

    const handleEngagement = (data: PublicationEngagementEvent) => {
      if (!isMountedRef.current) return;
      const event: PublicationEngagementEvent = {
        ...data,
        timestamp: data.timestamp ?? new Date().toISOString(),
      };
      setEngagementEvents((prev) =>
        [event, ...prev].slice(0, maxEngagementEventsRef.current)
      );
    };

    const handleConnection = (connectionData: { status: string }) => {
      if (!isMountedRef.current) return;
      setIsConnected(connectionData.status === 'connected');
    };

    // Acquire the shared connection and subscribe to the live analytics
    // topic. If another consumer already holds the connection open, this
    // just merges in our topic rather than opening a second socket.
    const token = useAuthStore.getState().token;
    const acquired = !!token;
    if (acquired) {
      websocketService.acquire(token!, [ANALYTICS_TOPIC]);
    } else if (websocketService.isConnected()) {
      websocketService.subscribe([ANALYTICS_TOPIC]);
    }

    // Register listeners
    websocketService.on('analytics_snapshot', handleSnapshot);
    websocketService.on('publication_engagement', handleEngagement);
    websocketService.on('connection', handleConnection);

    // Reflect current connection state immediately
    setIsConnected(websocketService.isConnected());

    return () => {
      isMountedRef.current = false;
      websocketService.unsubscribe([ANALYTICS_TOPIC]);
      websocketService.off('analytics_snapshot', handleSnapshot);
      websocketService.off('publication_engagement', handleEngagement);
      websocketService.off('connection', handleConnection);
      if (acquired) {
        websocketService.release();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — all deps are accessed via stable refs

  return { liveSnapshot, engagementEvents, isConnected, lastUpdated };
}

export default useAnalyticsRealtime;
